import { DialogFooter } from '@fluentui/react';
import { IPDefaultButton, IPModal, IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import TimePicker from 'components/Pickers/TimePicker';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, globalTextStylesValidationError } from 'globalStyles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IModifyTextDialogProps {
  isActionPending?: boolean;
  value?: Date;
  isOpen: boolean;
  title: string;
  subTitle?: string;
  label?: string;
  allowChangeTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
  onClose: () => void;
  onUpdate: (newValue: Date) => void;
  onSave: () => void;
  onGetErrorMessage?: (value: Date | undefined) => string | undefined;
}

const ModifyDateDialog = (props: IModifyTextDialogProps) => {
  const { t } = useTranslation(['translation', 'task']);
  const [error, setError] = useState<string | undefined>(undefined);

  const cannotSave = (): boolean => {
    if (props.isActionPending) return true;
    if (props.onGetErrorMessage && props.onGetErrorMessage(props.value) !== undefined) return true;

    return false;
  };

  return (
    <IPModal isBlocking={false} isOpen={props.isOpen} onDismiss={props.onClose}>
      <IPStack
        verticalFill
        tokens={globalStackTokensGapMedium}
        styles={{ root: { padding: 20, maxWidth: 400, minWidth: 300 } }}
      >
        <IPText variant="xLarge">{props.title}</IPText>
        <IPText>{props.subTitle}</IPText>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPStack.Item grow>
            <LocalizedDatePicker
              label={props.label}
              value={props.value}
              minDate={props.minDate}
              maxDate={props.maxDate}
              showWeekNumbers={true}
              onDateChange={(value) => {
                if (value && props.value) {
                  const newDate = value;
                  newDate.setHours(props.value.getHours());
                  newDate.setMinutes(props.value.getMinutes());
                  props.onUpdate(newDate);
                  if (props.onGetErrorMessage) {
                    setError(props.onGetErrorMessage(newDate));
                  }
                }
              }}
            />
          </IPStack.Item>
          {props.allowChangeTime && props.value && (
            <IPStack.Item>
              <TimePicker
                pickerWidth={100}
                selectedTime={props.value}
                onUpdateTime={(hours, mins) => {
                  if (props.value) {
                    const newDate = new Date(props.value);
                    newDate.setHours(hours);
                    newDate.setMinutes(mins);
                    props.onUpdate(newDate);
                    if (props.onGetErrorMessage) {
                      setError(props.onGetErrorMessage(newDate));
                    }
                  }
                }}
              />
            </IPStack.Item>
          )}
        </IPStack>
        <IPStack.Item>
          <IPText styles={globalTextStylesValidationError}>{error}</IPText>
        </IPStack.Item>
        <DialogFooter>
          <IPPrimaryButton
            disabled={cannotSave()}
            onClick={() => {
              props.onSave();
              props.onClose();
            }}
            text={t('translation:General.Button.Save')}
          />
          <IPDefaultButton
            disabled={props.isActionPending}
            onClick={() => {
              props.onClose();
            }}
            text={t('translation:General.Button.Cancel')}
          ></IPDefaultButton>
        </DialogFooter>
      </IPStack>
    </IPModal>
  );
};

export default ModifyDateDialog;
